/**
 * The core of `c.ai` (§4 `aiActions`, §14.68): the `ai` connector preset and model allowlist, the
 * request rules, one Anthropic Messages call through Vercel AI Gateway with the provider pinned, the
 * answer's text and usage, JSON extraction, and the per-circle daily cap arithmetic. No Convex
 * context: the actions authorise, read the connector and meter; everything here is pure or takes an
 * injected `fetch`, and is unit-tested in convex/test/ai.test.ts.
 *
 * The connector's secret only goes into the outgoing header; every string that comes back (the
 * answer, error messages) passes through `redactSecret`.
 */
import { connectorHostProblem, formatBytes, readCapped, redactSecret, type ConnectorTarget, type FetchImpl } from './connectorFetch';
import { fail, invalid } from './errors';
import { zonedParts, zonedToUtc } from './time';

/** The connector `c.ai` reads. An app declares it like any other: `"connectors": ["ai"]`. */
export const AI_CONNECTOR = 'ai';
export const AI_GATEWAY_URL = 'https://ai-gateway.vercel.sh';

/** Gateway slugs (`GET <gateway>/v1/models`, 2026-11-01) an owner may pick; the first is the default. */
export const AI_MODELS = [
  { id: 'anthropic/claude-haiku-5.5', label: 'Claude Haiku 5.5', note: 'Fast and cheapest; right for almost every app' },
  { id: 'anthropic/claude-sonnet-5.5', label: 'Claude Sonnet 5.5', note: 'Better at long or tricky answers; about 3× the cost' },
  { id: 'anthropic/claude-opus-5.5', label: 'Claude Opus 5.5', note: 'The strongest; about 5× the cost of Haiku' },
] as const;
export type AiModel = (typeof AI_MODELS)[number]['id'];
export const DEFAULT_AI_MODEL: AiModel = 'anthropic/claude-haiku-5.5';

/** What `connectors.presets` offers the builder's Connectors section for one-click setup. */
export const AI_PRESET = {
  baseUrl: AI_GATEWAY_URL,
  authHeader: 'Authorization',
  secretHint: 'Bearer <AI Gateway key>',
  models: AI_MODELS.map((m) => ({ id: m.id, label: m.label, note: m.note })),
  defaultModel: DEFAULT_AI_MODEL,
};

export const AI_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
export type AiImageType = (typeof AI_IMAGE_TYPES)[number];

export const MAX_AI_PROMPT_BYTES = 32 * 1024;
export const MAX_AI_SYSTEM_BYTES = 32 * 1024;
export const MAX_AI_IMAGES = 4;
export const MAX_AI_IMAGE_BYTES = 1.5 * 1024 * 1024;
export const MAX_AI_TOKENS = 2048;
export const DEFAULT_AI_TOKENS = 1024;
export const AI_TIMEOUT_MS = 30_000;
const MAX_AI_RESPONSE_BYTES = 1024 * 1024;
const ANTHROPIC_VERSION = '2023-06-01';
const USER_AGENT = 'BoltCircles-AI/1.0';

/** Appended to the system prompt for `json: true`. */
export const JSON_INSTRUCTION =
  'Answer with a single JSON value and nothing else: no prose before or after it, no Markdown code fences.';

export const DEFAULT_AI_DAILY_TOKEN_CAP = 200_000;
export const DEFAULT_AI_DAILY_CALL_CAP = 500;

export function isAiModel(model: unknown): model is AiModel {
  return typeof model === 'string' && AI_MODELS.some((m) => m.id === model);
}

// ---------- the request ----------

export type AiImage = { mediaType: AiImageType; data: string };
export type AiRequestInput = { prompt: unknown; system?: unknown; images?: unknown; json?: unknown; maxTokens?: unknown };
export type AiRequest = { prompt: string; system?: string; images: AiImage[]; json: boolean; maxTokens: number };

const BASE64_RE = /^[A-Za-z0-9+/]*={0,2}$/;

function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

/** Decoded size of a base64 string, or -1 when it is not padded base64. */
export function base64Size(data: string): number {
  if (data.length % 4 !== 0 || !BASE64_RE.test(data)) return -1;
  const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0;
  return (data.length / 4) * 3 - padding;
}

/**
 * Every rule on the app's side of a call, in one place; throws `INVALID` with the rule. `prompt`
 * 1 character–32 KB of UTF-8, `system` ≤ 32 KB, ≤ 4 images of ≤ 1.5 MB (decoded) each as padded
 * base64 of a JPEG, PNG, WebP or GIF, `maxTokens` an integer 1–2048 (default 1024).
 */
export function checkAiRequest(input: AiRequestInput): AiRequest {
  const { prompt, system, images, json, maxTokens } = input;
  if (typeof prompt !== 'string' || prompt.trim().length === 0) invalid('prompt must be a non-empty string');
  if (utf8Bytes(prompt) > MAX_AI_PROMPT_BYTES) invalid(`prompt must be at most ${formatBytes(MAX_AI_PROMPT_BYTES)}; keep prompts short`);
  if (system !== undefined && system !== null && typeof system !== 'string') invalid('system must be a string');
  if (typeof system === 'string' && utf8Bytes(system) > MAX_AI_SYSTEM_BYTES) invalid(`system must be at most ${formatBytes(MAX_AI_SYSTEM_BYTES)}`);
  if (json !== undefined && json !== null && typeof json !== 'boolean') invalid('json must be true or false');
  let tokens = DEFAULT_AI_TOKENS;
  if (maxTokens !== undefined && maxTokens !== null) {
    if (typeof maxTokens !== 'number' || !Number.isInteger(maxTokens) || maxTokens < 1 || maxTokens > MAX_AI_TOKENS) {
      invalid(`maxTokens must be an integer from 1 to ${MAX_AI_TOKENS}`);
    }
    tokens = maxTokens;
  }
  const checkedImages: AiImage[] = [];
  if (images !== undefined && images !== null) {
    if (!Array.isArray(images)) invalid('images must be an array of { mediaType, data }');
    if (images.length > MAX_AI_IMAGES) invalid(`at most ${MAX_AI_IMAGES} images per call`);
    images.forEach((image: unknown, i) => {
      const o = (image ?? {}) as { mediaType?: unknown; data?: unknown };
      if (typeof o.mediaType !== 'string' || !(AI_IMAGE_TYPES as readonly string[]).includes(o.mediaType)) {
        invalid(`images[${i}].mediaType must be one of ${AI_IMAGE_TYPES.join(', ')}`);
      }
      const size = typeof o.data === 'string' ? base64Size(o.data) : -1;
      if (size <= 0) invalid(`images[${i}].data must be the image's bytes as base64`);
      if (size > MAX_AI_IMAGE_BYTES) invalid(`images[${i}] is ${formatBytes(size)}; at most ${formatBytes(MAX_AI_IMAGE_BYTES)} (scale it down first)`);
      checkedImages.push({ mediaType: o.mediaType as AiImageType, data: o.data as string });
    });
  }
  return {
    prompt,
    ...(typeof system === 'string' && system.length > 0 ? { system } : {}),
    images: checkedImages,
    json: json === true,
    maxTokens: tokens,
  };
}

/** `<base>/v1/messages`; a base URL that already ends in `/v1` gets `/messages`. */
export function aiMessagesUrl(baseUrl: string): string {
  return baseUrl.endsWith('/v1') ? `${baseUrl}/messages` : `${baseUrl}/v1/messages`;
}

/** Anthropic's own API (no gateway): bare model ids and no `providerOptions`. */
function isDirectAnthropic(baseUrl: string): boolean {
  try {
    return new URL(baseUrl).hostname === 'api.anthropic.com';
  } catch {
    return false;
  }
}

/** `anthropic/claude-haiku-5.5` → `claude-haiku-5-5`, for Anthropic's API directly (the agent's `bareModelId`). */
export function bareModelId(slug: string): string {
  return slug.replace(/^anthropic\//, '').replace(/-(\d+)\.(\d{1,2})$/, '-$1-$2');
}

export type PreparedAiCall = {
  target: ConnectorTarget;
  model: AiModel;
  url: string;
  init: { method: 'POST'; headers: Record<string, string>; body: string };
};

/**
 * The upstream request for `req`: the Messages API at `aiMessagesUrl(baseUrl)`, the connector's header
 * with the secret, `anthropic-version`, the gateway slug, and `providerOptions.gateway` pinned to
 * Anthropic (`order` + `only`, as `agent/src/gateway.ts`, §14.55) so the gateway never falls back to
 * another provider. `json` appends `JSON_INSTRUCTION` to the system prompt. A base URL on a private
 * network is `FORBIDDEN` before anything is sent.
 */
export function prepareAiCall(target: ConnectorTarget, req: AiRequest, model: AiModel): PreparedAiCall {
  const problem = connectorHostProblem(target.baseUrl);
  if (problem) {
    fail('FORBIDDEN', `Connector "${target.name}" points at ${problem}, which connectors may not call; the owner changes its base URL in the builder → Connectors`);
  }
  const direct = isDirectAnthropic(target.baseUrl);
  const system = [req.system, req.json ? JSON_INSTRUCTION : undefined].filter((s): s is string => !!s).join('\n\n');
  const content = [
    ...req.images.map((img) => ({ type: 'image', source: { type: 'base64', media_type: img.mediaType, data: img.data } })),
    { type: 'text', text: req.prompt },
  ];
  const body = {
    model: direct ? bareModelId(model) : model,
    max_tokens: req.maxTokens,
    ...(system ? { system } : {}),
    messages: [{ role: 'user', content }],
    ...(direct ? {} : { providerOptions: { gateway: { order: ['anthropic'], only: ['anthropic'] } } }),
  };
  const headers: Record<string, string> = { 'content-type': 'application/json', 'anthropic-version': ANTHROPIC_VERSION };
  if (target.authHeader.toLowerCase() !== 'user-agent') headers['user-agent'] = USER_AGENT;
  headers[target.authHeader] = target.secret;
  return { target, model, url: aiMessagesUrl(target.baseUrl), init: { method: 'POST', headers, body: JSON.stringify(body) } };
}

// ---------- the answer ----------

export type AiUsage = { inputTokens: number; outputTokens: number };
export type AiCompletion = { text: string; jsonText?: string; model: AiModel; usage: AiUsage };
export type AiLimits = { timeoutMs: number; maxResponseBytes: number };
export const DEFAULT_AI_LIMITS: AiLimits = { timeoutMs: AI_TIMEOUT_MS, maxResponseBytes: MAX_AI_RESPONSE_BYTES };

const count = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : 0);

/** Anthropic's `{ type: 'error', error: { message } }`, else the start of the body. */
function upstreamMessage(body: string): string {
  try {
    const data = JSON.parse(body) as { error?: { message?: unknown } | string; message?: unknown };
    const message = typeof data.error === 'string' ? data.error : (data.error?.message ?? data.message);
    if (typeof message === 'string' && message.length > 0) return message.slice(0, 300);
  } catch {
    // not JSON
  }
  return body.trim().slice(0, 300) || 'no details';
}

/**
 * A Messages API answer → the text (every `text` block, joined), the usage (input counts the prompt
 * cache's reads and writes too) and, for `json`, the JSON in the text. Errors: 429 → `RATE_LIMITED`;
 * 400/413/422 → `INVALID` with the upstream's reason; any other non-2xx, a 3xx or a body that is not
 * a message → `UPSTREAM_FAILED`. Every string is redacted.
 */
export function parseAiResponse(status: number, body: string, prepared: Pick<PreparedAiCall, 'target' | 'model'>, json: boolean): AiCompletion {
  const { secret } = prepared.target;
  if (status === 429) fail('RATE_LIMITED', redactSecret(`The AI service is rate limiting this circle (429: ${upstreamMessage(body)}); try again in a minute`, secret));
  if (status === 400 || status === 413 || status === 422) fail('INVALID', redactSecret(`The AI service refused the request (${status}): ${upstreamMessage(body)}`, secret));
  if (status < 200 || status > 299) fail('UPSTREAM_FAILED', redactSecret(`The AI service answered ${status}: ${upstreamMessage(body)}`, secret));
  let data: { content?: unknown; usage?: Record<string, unknown> };
  try {
    data = JSON.parse(body) as typeof data;
  } catch {
    fail('UPSTREAM_FAILED', 'The AI service answered with something that is not a message');
  }
  if (!data || typeof data !== 'object' || !Array.isArray(data.content)) fail('UPSTREAM_FAILED', 'The AI service answered with something that is not a message');
  const text = redactSecret(
    data.content
      .filter((b): b is { type: 'text'; text: string } => !!b && typeof b === 'object' && (b as { type?: unknown }).type === 'text' && typeof (b as { text?: unknown }).text === 'string')
      .map((b) => b.text)
      .join(''),
    secret,
  );
  const u = data.usage ?? {};
  const usage = { inputTokens: count(u.input_tokens) + count(u.cache_creation_input_tokens) + count(u.cache_read_input_tokens), outputTokens: count(u.output_tokens) };
  const jsonText = json ? extractJsonText(text) : undefined;
  return { text, ...(jsonText !== undefined ? { jsonText } : {}), model: prepared.model, usage };
}

/**
 * Makes the call: `redirect: 'manual'` (a redirect is `UPSTREAM_FAILED`; the key never follows one),
 * `UPSTREAM_TIMEOUT` after `timeoutMs` (connect and body), `UPSTREAM_TOO_LARGE` past
 * `maxResponseBytes`, `UPSTREAM_FAILED` when the service cannot be reached; then `parseAiResponse`.
 * Everything this throws happened at (or on the way to) the upstream, so the caller meters it.
 */
export async function sendAiCall(prepared: PreparedAiCall, json: boolean, fetchImpl: FetchImpl, limits: AiLimits = DEFAULT_AI_LIMITS): Promise<AiCompletion> {
  const { target } = prepared;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), limits.timeoutMs);
  try {
    let res: Response;
    let bytes: Uint8Array | null;
    try {
      res = await fetchImpl(prepared.url, { ...prepared.init, redirect: 'manual', signal: controller.signal });
      bytes = await readCapped(res, limits.maxResponseBytes);
    } catch (err) {
      if (controller.signal.aborted) fail('UPSTREAM_TIMEOUT', `The AI service did not answer within ${Math.round(limits.timeoutMs / 1000)} s`);
      const reason = err instanceof Error ? err.message : String(err);
      fail('UPSTREAM_FAILED', redactSecret(`Could not reach the AI service: ${reason}`, target.secret));
    }
    if (!bytes) fail('UPSTREAM_TOO_LARGE', `The AI service answered with more than ${formatBytes(limits.maxResponseBytes)}`);
    return parseAiResponse(res.status, new TextDecoder().decode(bytes), prepared, json);
  } finally {
    clearTimeout(timer);
  }
}

// ---------- JSON in the answer ----------

function parses(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

/** Index of the bracket closing the `{`/`[` at `start`, skipping strings; -1 when it never closes. */
function closingIndex(text: string, start: number): number {
  let depth = 0;
  let inString = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (ch === '\\') i++;
      else if (ch === '"') inString = false;
    } else if (ch === '"') {
      inString = true;
    } else if (ch === '{' || ch === '[') {
      depth++;
    } else if (ch === '}' || ch === ']') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/**
 * The first JSON value in a model's answer, as text: the whole answer when it parses (any JSON value),
 * else the first `{…}` or `[…]` that does (so prose or Markdown fences around it do not matter).
 * `undefined` when there is none. Returned as text, not parsed: Convex values cannot carry every JSON
 * object (keys starting with `$`, non-ASCII keys), so the SDKs parse it (§14.68).
 */
export function extractJsonText(text: string): string | undefined {
  const trimmed = text.trim();
  if (trimmed.length > 0 && parses(trimmed)) return trimmed;
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (ch !== '{' && ch !== '[') continue;
    const end = closingIndex(trimmed, i);
    if (end < 0) continue;
    const candidate = trimmed.slice(i, end + 1);
    if (parses(candidate)) return candidate;
  }
  return undefined;
}

// ---------- the per-circle daily cap ----------

export type AiCaps = { tokens: number; calls: number };
export type AiDayUsage = { calls: number; inputTokens: number; outputTokens: number };

function capFrom(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw.trim() === '') return fallback;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : fallback;
}

/** `AI_DAILY_TOKEN_CAP` / `AI_DAILY_CALL_CAP` (non-negative integers; `0` turns AI off), else the defaults. */
export function aiCaps(env: Record<string, string | undefined>): AiCaps {
  return {
    tokens: capFrom(env.AI_DAILY_TOKEN_CAP, DEFAULT_AI_DAILY_TOKEN_CAP),
    calls: capFrom(env.AI_DAILY_CALL_CAP, DEFAULT_AI_DAILY_CALL_CAP),
  };
}

/** The circle's calendar day at `ms`, `YYYY-MM-DD` in `timeZone`: the `aiUsage` row a call counts against. */
export function aiDay(ms: number, timeZone: string): string {
  const p = zonedParts(ms, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** The next midnight in `timeZone` after `ms`, when the day's allowance starts again. */
export function aiDayResetsAt(ms: number, timeZone: string): number {
  const p = zonedParts(ms, timeZone);
  return zonedToUtc(p.year, p.month, p.day + 1, 0, 0, timeZone);
}

/** Why another call must wait for tomorrow, or null: today's tokens (in + out) or calls are at the cap. */
export function aiCapProblem(usage: AiDayUsage | null, caps: AiCaps, timeZone: string): string | null {
  const used = usage ?? { calls: 0, inputTokens: 0, outputTokens: 0 };
  const resets = `it starts again at midnight (${timeZone})`;
  if (used.calls >= caps.calls) return `This circle has used today's AI allowance (${caps.calls} calls); ${resets}`;
  if (used.inputTokens + used.outputTokens >= caps.tokens) return `This circle has used today's AI allowance (${caps.tokens} tokens); ${resets}`;
  return null;
}
